/* eslint-disable react/prop-types */
// eslint-disable-next-line no-unused-vars
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment-timezone';
import useUser from 'core/hooks/useUser';
import Money from 'core/api/Money';

import { CANCEL_SUBSCRIPTION, PAUSE_SUBSCRIPTION, RESUME_SUBSCRIPTION } from 'core/api/apollo/mutations';
import { LIST_SUBSCRIPTION_STATUS_REASONS, GET_BILLING_INFO } from 'core/api/apollo/queries';

const C = {
  greenDark: '#2D3A2E', greenMed: '#4A6741', greenLight: '#E8EDE8', greenAccent: '#A8C5A0',
  bg: '#FAFAF8', white: '#FFFFFF', text: '#1A1A1A', muted: '#6B7280', border: '#E0E0E0',
  cardBorder: '#D4D4D4', red: '#DC2626', redLight: '#FEF2F2', redBorder: '#FECACA',
  amber: '#92400E', amberLight: '#FFFBEB', amberBorder: '#FCD34D',
};
const font = 'system-ui, -apple-system, sans-serif';

const Card = ({ title, children, style: s }) => (
  <div style={{ border: '1px solid ' + C.cardBorder, borderRadius: 8, background: C.white, ...s }}>
    <div style={{ padding: '12px 20px', borderBottom: '1px solid ' + C.border }}>
      <span style={{ fontSize: 14, fontFamily: font, fontWeight: 600, color: C.text }}>{title}</span>
    </div>
    <div style={{ padding: 20 }}>{children}</div>
  </div>
);

const BackBtn = ({ onClick, label }) => (
  <button onClick={onClick} style={{ fontSize: 13, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', fontFamily: font, marginBottom: 20, padding: 0 }}>{'<-'} {label || 'Back'}</button>
);

const CancelFlow = ({ className }) => {
  const { currentUser, refetchUser } = useUser();
  const subscription = currentUser?.subscription;
  const nextDeliveryDate = currentUser?.subscription?.nextDeliveryDate;
  const lastInvoicedDate = currentUser?.subscription?.lastInvoicedDate;
  const cancellationEffectiveDateRaw = currentUser?.subscription?.cancellationEffectiveDate;
  const pauseEffectiveDateRaw = currentUser?.subscription?.pauseEffectiveDate;
  const estimatedAmount = currentUser?.subscription?.estimatedPrice;
  const sizeName = subscription?.productSize?.name;
  const categoryName = subscription?.subscriptionQuantities?.[0]?.subscriptionCategory?.name;
  const subscriptionName = sizeName && categoryName ? `${sizeName} - ${categoryName}` : '';
  const subscriptionStatus = currentUser?.subscription?.status;
  const indefinitelyPaused = currentUser?.subscription?.indefinitelyPaused;
  const pausedNextWeek = currentUser?.subscription?.pausedNextWeek;
  const pauseStartDate = currentUser?.subscription?.pauseStartDate;
  const pauseEndDate = currentUser?.subscription?.pauseEndDate;
  const pauseStartFormatted = pauseStartDate ? moment(pauseStartDate).format('MMMM D') : '';
  const pauseEndFormatted = pauseEndDate ? moment(pauseEndDate).format('MMMM D') : '';
  const hasTimeBoundPause = pausedNextWeek && !indefinitelyPaused && pauseStartDate;
  const cutoffPassed = nextDeliveryDate && moment(nextDeliveryDate).subtract(8, 'days').isBefore(moment());
  const hasLockedDelivery = indefinitelyPaused && !!nextDeliveryDate && cutoffPassed;
  const lockedDeliveryFormatted = nextDeliveryDate ? moment(nextDeliveryDate).format('dddd, MMMM D') : '';
  const cutoffDate = nextDeliveryDate ? moment(nextDeliveryDate).subtract(8, 'days') : null;
  const cutoffFormatted = cutoffDate ? cutoffDate.format('dddd, MMMM D') : '';

  const { data: reasonsData } = useQuery(LIST_SUBSCRIPTION_STATUS_REASONS);
  const reasons = reasonsData?.listSubscriptionStatusReasons || [];

  const { data: billingData } = useQuery(GET_BILLING_INFO);
  const availableCredits = billingData?.getBillingInfo?.availableCredits;
  const creditsAmount = availableCredits?.amount || 0;
  const creditsFormatted = Money(availableCredits).toFormat();

  const [screen, setScreen] = useState('billing');
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [memo, setMemo] = useState('');
  const [resumeDate, setResumeDate] = useState('');
  const [pauseLoading, setPauseLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);

  const selectedReason = reasons.find((r) => r.id === selectedReasonId);

  const nextDelivery = nextDeliveryDate ? moment(nextDeliveryDate) : null;
  const nextDeliveryFormatted = nextDelivery ? nextDelivery.format('MMMM D') : '';
  const lastInvoicedDateFormatted = lastInvoicedDate && moment(lastInvoicedDate).format('dddd, MMMM D');
  const cancellationEffectiveDate = cancellationEffectiveDateRaw ? moment(cancellationEffectiveDateRaw).format('MMMM D') : '';
  const pauseEffectiveDate = pauseEffectiveDateRaw ? moment(pauseEffectiveDateRaw).format('MMMM D') : '';
  const pauseEffectiveMoment = pauseEffectiveDateRaw ? moment(pauseEffectiveDateRaw) : null;
  const nextDeliveryIsLocked = nextDeliveryDate && pauseEffectiveMoment && moment(nextDeliveryDate).isBefore(pauseEffectiveMoment);
  const resumeDateOptions = pauseEffectiveDateRaw
    ? [0, 1, 2, 3]
        .map((w) => moment(pauseEffectiveDateRaw).add(w, 'weeks'))
        .filter((d) => d.isAfter(moment(), 'day'))
        .map((d) => d.format('MMMM D, YYYY'))
    : [];

  const [updateUserOtpSubscription] = useMutation(CANCEL_SUBSCRIPTION, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('User OTP cancelled successfully:', data);
      refetchUser();
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error cancelling user OTP:', error);
    },
  });

  const [pauseSubscription] = useMutation(PAUSE_SUBSCRIPTION, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Subscription paused successfully:', data);
      refetchUser();
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error pausing subscription:', error);
    },
  });

  const [resumeSubscription] = useMutation(RESUME_SUBSCRIPTION, {
    onCompleted: async (data) => {
      // eslint-disable-next-line no-console
      console.log('Subscription resumed successfully:', data);
      refetchUser();
    },
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error resuming subscription:', error);
    },
  });

  const handleResume = async () => {
    setResumeLoading(true);
    try {
      const { data } = await resumeSubscription({ variables: { input: {} } });
      const errors = data?.resumeSubscription?.errors || [];
      if (errors.length > 0) {
        // eslint-disable-next-line no-console
        console.error('Resume failed:', errors);
        return;
      }
      refetchUser();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to resume subscription:', error);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleCancellation = async () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    try {
      const { data } = await updateUserOtpSubscription({
        variables: { input: {
          reasonId: selectedReasonId,
          memo: memo || undefined,
        } },
      });
      const errors = data?.cancelSubscription?.errors || [];
      if (errors.length > 0) {
        // eslint-disable-next-line no-console
        console.error('Cancellation failed:', errors);
        return;
      }
      refetchUser();
      setScreen('cancel-done');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to cancel subscription:', error);
    } finally {
      setCancelLoading(false);
    }
  };

  const handlePause = async () => {
    setPauseLoading(true);
    try {
      const startDate = pauseEffectiveDateRaw
        ? moment(pauseEffectiveDateRaw).format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
      const input = { startDate };
      if (resumeDate) {
        input.endDate = moment(resumeDate, 'MMMM D, YYYY').format('YYYY-MM-DD');
      }

      const { data } = await pauseSubscription({
        variables: { input },
      });
      const errors = data?.pauseSubscription?.errors || [];
      if (errors.length > 0) {
        // eslint-disable-next-line no-console
        console.error('Pause failed:', errors);
        return;
      }
      refetchUser();
      setScreen('pause-done');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to pause subscription:', error);
    } finally {
      setPauseLoading(false);
    }
  };

  const renderBilling = () => (
    <div style={{ maxWidth: 1080, margin: '0 auto' }}>
      <Card title="Subscription" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, fontFamily: font, color: C.text, marginBottom: 4 }}>{subscriptionName}</div>
            {subscriptionStatus !== 'cancelled' && (
              <div style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6 }}>
                Weekly subscription<br/>
                {indefinitelyPaused
                  ? hasLockedDelivery && <>Next delivery: {nextDeliveryFormatted}</>
                  : <>{estimatedAmount && Money(estimatedAmount).toFormat('$0,0')}/week, Next delivery: {nextDeliveryFormatted}</>
                }
              </div>
            )}
          </div>
          <span style={{ background: subscriptionStatus === 'cancelled' ? C.redLight : (indefinitelyPaused || hasTimeBoundPause) ? C.amberLight : '#DCFCE7', color: subscriptionStatus === 'cancelled' ? C.red : (indefinitelyPaused || hasTimeBoundPause) ? C.amber : '#166534', fontSize: 12, padding: '3px 10px', borderRadius: 12, fontFamily: font, fontWeight: 600, flexShrink: 0 }}>{indefinitelyPaused ? 'paused' : hasTimeBoundPause ? 'paused' : subscriptionStatus}</span>
        </div>
        {indefinitelyPaused && (
          <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 8, padding: 16, marginTop: 16, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.6 }}>
            {hasLockedDelivery ? (
              <>Your subscription is <strong>paused indefinitely</strong>. You still have one delivery scheduled for <strong>{lockedDeliveryFormatted}</strong>. The deadline to pause that delivery passed at 11 PM PT on {cutoffFormatted}, as described in our <a href="https://gomethodology.com/terms-conditions" rel="noreferrer" target="_blank" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms and Conditions</a>. No charges or deliveries will occur following that delivery. Your program and preferences are all saved.</>
            ) : (
              <>Your subscription is <strong>paused indefinitely</strong>. No charges or deliveries will occur until you resume. Your program and preferences are all saved.</>
            )}
            <div style={{ marginTop: 12 }}>
              <button
                disabled={resumeLoading}
                onClick={handleResume}
                style={{ fontSize: 13, fontFamily: font, color: C.white, background: resumeLoading ? C.greenAccent : C.greenMed, border: 'none', padding: '8px 20px', borderRadius: 5, cursor: resumeLoading ? 'not-allowed' : 'pointer', fontWeight: 600 }}>{resumeLoading ? 'Unpausing...' : 'Unpause My Subscription'}</button>
            </div>
          </div>
        )}
        {hasTimeBoundPause && (
          <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 8, padding: 16, marginTop: 16, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.6 }}>
            Your subscription is <strong>paused from the week of {pauseStartFormatted}{pauseEndFormatted && pauseStartFormatted !== pauseEndFormatted ? ` through ${pauseEndFormatted}` : ''}</strong>. No charges or deliveries during this period.
            {nextDeliveryDate && (
              <div style={{ marginTop: 8 }}>Your delivery for the week of <strong>{moment(nextDeliveryDate).format('MMMM D')}</strong> has already been finalized and will arrive as scheduled.</div>
            )}
            <div style={{ marginTop: 8, color: C.greenMed }}>Deliveries will automatically resume after the pause period.</div>
          </div>
        )}
        {subscriptionStatus !== 'cancelled' && !indefinitelyPaused && (
          <div style={{ borderTop: '1px solid ' + C.border, marginTop: 16, paddingTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button
              onClick={() => setScreen('pause-1')}
              style={{ fontSize: 13, fontFamily: font, color: C.greenMed, background: 'none', border: '1px solid ' + C.greenMed, padding: '7px 18px', borderRadius: 5, cursor: 'pointer' }}>Pause Subscription</button>
            <button
              onClick={() => setScreen('cancel-1')}
              style={{ fontSize: 13, fontFamily: font, color: C.red, background: 'none', border: '1px solid ' + C.redBorder, padding: '7px 18px', borderRadius: 5, cursor: 'pointer', marginLeft: 'auto' }}>Cancel Subscription</button>
          </div>
        )}
        <div style={{ marginTop: 16, fontSize: 12, fontFamily: font, color: C.muted, lineHeight: 1.6 }}>
          Your subscription renews automatically each week unless you pause or cancel. Payment is processed each Sunday, and your Monday delivery that is 8 days out is finalized at that time. Any changes must be made by Sunday at 11 PM PT and take effect for the Monday delivery that is 8 days after that.{' '}
          <span style={{ color: C.greenMed, textDecoration: 'underline', cursor: 'pointer' }}>
            <a href='https://gomethodology.com/terms-conditions' rel="noreferrer" target='_blank'>Terms and Conditions</a>
          </span>
        </div>
      </Card>
    </div>
  );

  const renderPauseStep1 = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '40px 24px' }}>
      <BackBtn onClick={() => setScreen('billing')} label="Back to Subscription" />
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>Pause Your Subscription</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, marginBottom: 24 }}>Take a break without losing anything. Your program, preferences, wellness coach, and pricing all stay locked in. No charges while paused. Come back whenever you are ready.</p>
      {nextDeliveryIsLocked ? (
        <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.6 }}>
          <strong>Heads up:</strong> Your delivery for the week of <strong>{nextDeliveryFormatted}</strong> (charged {lastInvoicedDateFormatted}) has already been finalized and will arrive as scheduled. Your pause takes effect starting the week of <strong>{pauseEffectiveDate}</strong>.
        </div>
      ) : (
        <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 8, padding: 16, marginBottom: 24, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.6 }}>
          Your pause takes effect starting the week of <strong>{pauseEffectiveDate}</strong>. No invoice has been generated yet for this week.
        </div>
      )}
      <div style={{ border: '1px solid ' + C.border, borderRadius: 8, padding: 20, marginBottom: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 600, fontFamily: font, color: C.text, marginBottom: 12 }}>When would you like to resume?</div>
        <p style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6, marginBottom: 16 }}>Pick a week and we will automatically restart your deliveries. You can change this later.</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          {resumeDateOptions.map((d) => (
            <button key={d} onClick={() => setResumeDate(d)} style={{ fontSize: 13, fontFamily: font, padding: '8px 16px', borderRadius: 6, cursor: 'pointer', background: resumeDate === d ? C.greenDark : C.white, color: resumeDate === d ? C.white : C.text, border: '1px solid ' + (resumeDate === d ? C.greenDark : C.border) }}>Week of {d}</button>
          ))}
        </div>
        <button onClick={() => setResumeDate('')} style={{ fontSize: 12, fontFamily: font, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>Not sure yet, I will resume manually</button>
      </div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button disabled={pauseLoading} onClick={handlePause} style={{ fontSize: 14, fontFamily: font, color: C.white, background: pauseLoading ? C.greenAccent : C.greenMed, border: 'none', padding: '11px 28px', borderRadius: 5, cursor: pauseLoading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: pauseLoading ? 0.8 : 1 }}>{pauseLoading ? 'Pausing...' : 'Pause My Subscription'}</button>
        <button disabled={pauseLoading} onClick={() => setScreen('billing')} style={{ fontSize: 14, fontFamily: font, color: C.muted, background: 'none', border: 'none', padding: '11px 16px', cursor: 'pointer' }}>Never mind</button>
      </div>
    </div>
  );

  const renderPauseDone = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 28, background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 24, color: C.greenMed }}>||</div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>Subscription Paused</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, maxWidth: 420, margin: '0 auto 24px' }}>
        {resumeDate ? 'You are all set. We will automatically restart your deliveries the week of ' + resumeDate + '. We will send you a reminder beforehand.' : 'You are all set. Your subscription is paused until you decide to come back. Resume anytime from Settings > Subscription.'}{nextDeliveryIsLocked && <> Your delivery for the week of {nextDeliveryFormatted} will still arrive as scheduled.</>}
      </p>
      <div style={{ background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 24, textAlign: 'left', maxWidth: 360, margin: '0 auto 24px' }}>
        <div style={{ fontSize: 12, fontFamily: font, color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Pause Details</div>
        <div style={{ fontSize: 13, fontFamily: font, color: C.text, lineHeight: 1.7 }}>
          Paused: {moment().format('MMMM D, YYYY')}<br/>{nextDeliveryIsLocked && <>Last delivery: Week of {nextDeliveryFormatted}<br/></>}{resumeDate ? 'Scheduled resume: Week of ' + resumeDate : 'Resume: Manual (anytime)'}<br/><span style={{ color: C.greenMed }}>Your program, preferences, and pricing are saved.</span>
        </div>
      </div>
      <button onClick={() => setScreen('billing')} style={{ fontSize: 13, fontFamily: font, color: C.greenMed, background: 'none', border: '1px solid ' + C.greenMed, padding: '8px 20px', borderRadius: 5, cursor: 'pointer' }}>Return to Settings</button>
    </div>
  );

  const renderCancelStep1 = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '40px 24px' }}>
      <BackBtn onClick={() => setScreen('billing')} label="Back to Subscription" />
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>Cancel Your Subscription</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, marginBottom: 28 }}>We are sorry to see you go. Before you cancel, would you consider pausing instead?</p>
      <div style={{ background: C.greenLight, border: '2px solid ' + C.greenAccent, borderRadius: 10, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.greenDark, fontFamily: font, marginBottom: 8 }}>Take a break instead?</div>
        <p style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.65, marginBottom: 6 }}>Pausing saves everything: your program, your preferences, your wellness coach, and your pricing. No charges while paused. Resume whenever you are ready with one click.</p>
        <p style={{ fontSize: 13, fontFamily: font, color: C.greenMed, lineHeight: 1.5, marginBottom: 16 }}>Most customers who pause come back within 3 weeks.</p>
        <button onClick={() => setScreen('pause-1')} style={{ fontSize: 14, fontFamily: font, color: C.white, background: C.greenMed, border: 'none', padding: '11px 28px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Pause My Subscription</button>
      </div>
      <div style={{ border: '1px solid ' + C.border, borderRadius: 8, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, fontFamily: font, color: C.text, marginBottom: 10 }}>Continue with cancellation</div>
        <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 6, padding: 12, marginBottom: 14, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.6 }}>
          Your delivery for the week of <strong>{nextDeliveryFormatted}</strong> was charged on <strong>{lastInvoicedDateFormatted}</strong> and has already been finalized. This delivery will still be shipped. Cancellation takes effect for the week of <strong>{cancellationEffectiveDate}</strong>.
        </div>
        <ul style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.8, paddingLeft: 18, marginBottom: 14 }}>
          <li>Your {nextDeliveryFormatted} delivery will arrive as scheduled.</li>
          <li>You will not be charged for {cancellationEffectiveDate} or any future weeks.</li>
          <li>You can resubscribe at any time.</li>
        </ul>
        <button onClick={() => setScreen('cancel-survey')} style={{ fontSize: 13, fontFamily: font, color: C.red, background: C.white, border: '1px solid ' + C.red, padding: '9px 22px', borderRadius: 5, cursor: 'pointer' }}>Cancel My Subscription</button>
      </div>
    </div>
  );

  const renderCancelSurvey = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '40px 24px' }}>
      <BackBtn onClick={() => setScreen('cancel-1')} label="Back" />
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>What is the main reason you are canceling?</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, marginBottom: 24 }}>This helps us improve Methodology for everyone. Select one.</p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 20 }}>
        {reasons.map((r) => (
          <label key={r.id} onClick={() => setSelectedReasonId(r.id)} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 6, cursor: 'pointer', fontFamily: font, fontSize: 13,
            border: '1px solid ' + (selectedReasonId === r.id ? C.greenMed : C.border),
            background: selectedReasonId === r.id ? C.greenLight : C.white, color: C.text, transition: 'all 0.15s',
          }}>
            <div style={{ width: 16, height: 16, borderRadius: 8, border: '2px solid ' + (selectedReasonId === r.id ? C.greenMed : C.cardBorder), background: selectedReasonId === r.id ? C.greenMed : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {selectedReasonId === r.id && <div style={{ width: 5, height: 5, borderRadius: 3, background: C.white }} />}
            </div>
            {r.reason}
          </label>
        ))}
      </div>
      {selectedReason?.requireMemo && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontFamily: font, color: C.muted, display: 'block', marginBottom: 6 }}>Anything else? (optional)</label>
          <textarea value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Your feedback helps us improve..." style={{ width: '100%', minHeight: 72, padding: 12, fontSize: 13, fontFamily: font, border: '1px solid ' + C.border, borderRadius: 6, resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
      )}
      <button disabled={!selectedReasonId} onClick={() => setScreen('cancel-confirm')} style={{
        fontSize: 14, fontFamily: font, padding: '10px 24px', borderRadius: 5, cursor: selectedReasonId ? 'pointer' : 'not-allowed', fontWeight: 500,
        color: selectedReasonId ? C.red : '#9CA3AF', background: C.white, border: '1px solid ' + (selectedReasonId ? C.red : '#D1D5DB'),
      }}>Continue with Cancellation</button>
    </div>
  );

  const renderCancelConfirm = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '40px 24px' }}>
      <BackBtn onClick={() => setScreen('cancel-survey')} label="Back" />
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>Confirm Cancellation</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, marginBottom: 20 }}>Please confirm you would like to cancel your Methodology subscription.</p>
      <div style={{ background: C.redLight, border: '1px solid ' + C.redBorder, borderRadius: 8, padding: 20, marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.7 }}>
          <strong>Program:</strong> {subscriptionName}<br/>
          <strong>Last delivery:</strong> Week of {nextDeliveryFormatted} (charged {lastInvoicedDateFormatted})<br/>
          <strong>First cancelled week:</strong> {cancellationEffectiveDate}<br/>
          <strong>No future charges</strong> will be applied.
        </div>
      </div>
      {creditsAmount > 0 && (
        <div style={{ background: C.amberLight, border: '1px solid ' + C.amberBorder, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, fontFamily: font, color: C.amber, lineHeight: 1.5 }}>
          You have <strong>{creditsFormatted} in credits</strong> on your account. Your credits will remain available if you resubscribe in the future.
        </div>
      )}
      <div style={{ border: '1px solid ' + C.border, borderRadius: 8, padding: 14, marginBottom: 16, fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6 }}>
        <div style={{ fontWeight: 600, color: C.text, marginBottom: 6, fontSize: 13 }}>When you cancel, you will lose access to:</div>
        <div>Your current program settings and meal preferences</div>
        <div>Your wellness coach relationship</div>
        <div>Weekly curated menus tailored to your goals</div>
        <div style={{ marginTop: 8, color: C.greenMed, fontWeight: 500 }}>Pausing preserves all of this. <span onClick={() => setScreen('pause-1')} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Pause instead?</span></div>
      </div>
      <div style={{ background: C.greenLight, border: '1px solid ' + C.greenAccent, borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, fontFamily: font, color: C.greenDark, lineHeight: 1.5 }}>
        You will continue to receive marketing emails and updates from Methodology. Manage preferences in
        <a href="/settings/account-info" style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}> Account Info &gt; Notifications</a>.
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <button disabled={cancelLoading} onClick={handleCancellation} style={{ fontSize: 14, fontFamily: font, color: C.white, background: cancelLoading ? '#EF4444' : C.red, border: 'none', padding: '11px 24px', borderRadius: 5, cursor: cancelLoading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: cancelLoading ? 0.8 : 1 }}>{cancelLoading ? 'Cancelling...' : 'Yes, Cancel My Subscription'}</button>
        <button onClick={() => setScreen('billing')} style={{ fontSize: 14, fontFamily: font, color: C.muted, background: C.white, border: '1px solid ' + C.border, padding: '11px 24px', borderRadius: 5, cursor: 'pointer' }}>Keep My Subscription</button>
      </div>
    </div>
  );

  const renderCancelDone = () => (
    <div style={{ maxWidth: 540, margin: '0 auto', padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ width: 56, height: 56, borderRadius: 28, background: C.greenLight, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 22, color: C.greenMed, fontWeight: 700 }}>OK</div>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: C.text, marginBottom: 6, fontWeight: 600 }}>Subscription Cancelled</h1>
      <p style={{ color: C.muted, fontSize: 14, fontFamily: font, lineHeight: 1.6, maxWidth: 420, margin: '0 auto 24px' }}>
        Your subscription has been cancelled. You will receive a confirmation email shortly. Your delivery for the week of {nextDeliveryFormatted} will still arrive as scheduled.
      </p>
      <div style={{ background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 16, marginBottom: 24, textAlign: 'left', maxWidth: 380, margin: '0 auto 24px' }}>
        <div style={{ fontSize: 12, fontFamily: font, color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600 }}>Confirmation</div>
        <div style={{ fontSize: 13, fontFamily: font, color: C.text, lineHeight: 1.7 }}>
          Cancelled: {moment().tz('America/Los_Angeles').format('MMMM D, YYYY [at] h:mm A')} PT<br/>
          Last delivery: Week of {nextDeliveryFormatted} (charged {lastInvoicedDateFormatted})<br/>
          First cancelled week: {cancellationEffectiveDate}<br/>
          {creditsAmount > 0 && <>Credits on account: {creditsFormatted}<br/></>}
        </div>
      </div>
      <p style={{ fontSize: 14, fontFamily: font, color: C.text, lineHeight: 1.6, marginBottom: 8, fontWeight: 500 }}>We would love to cook for you again.</p>
      <p style={{ fontSize: 13, fontFamily: font, color: C.muted, lineHeight: 1.6, marginBottom: 20 }}>You can resubscribe anytime.{creditsAmount > 0 && ` Your ${creditsFormatted} in credits will be waiting for you.`}</p>
      <button onClick={() => setScreen('billing')} style={{ fontSize: 13, fontFamily: font, color: C.greenMed, background: 'none', border: '1px solid ' + C.greenMed, padding: '10px 20px', borderRadius: 5, cursor: 'pointer' }}>Return to Settings</button>
    </div>
  );

  const renderScreen = () => {
    switch (screen) {
      case 'billing': return renderBilling();
      case 'pause-1': return renderPauseStep1();
      case 'pause-done': return renderPauseDone();
      case 'cancel-1': return renderCancelStep1();
      case 'cancel-survey': return renderCancelSurvey();
      case 'cancel-confirm': return renderCancelConfirm();
      case 'cancel-done': return renderCancelDone();
      default: return renderBilling();
    }
  };

  return (
    <div className={className} style={{ background: C.bg }}>
      {renderScreen()}
    </div>
  );
};

export default CancelFlow;
